import { createElement } from '../utils';
import { MarkdownFormatter } from '../utils/markdown';

/**
 * Formatting toolbar options
 */
interface FormattingToolbarOptions {
  textarea: HTMLTextAreaElement;
  onPreview: () => void;
  theme?: 'light' | 'dark';
}

/**
 * Formatting toolbar for Markdown support
 * Displays above textarea with formatting buttons and preview toggle
 */
export class FormattingToolbar {
  private container: HTMLElement;
  private options: FormattingToolbarOptions;
  private buttons: Map<string, HTMLButtonElement> = new Map();

  constructor(options: FormattingToolbarOptions) {
    this.options = {
      theme: 'light',
      ...options
    };
    
    this.container = this.createToolbar();
    this.setupEventListeners();
    this.addStyles();
  }

  /**
   * Create toolbar element with formatting buttons
   */
  private createToolbar(): HTMLElement {
    const toolbar = createElement('div', { 
      className: 'komentas-formatting-toolbar',
      role: 'toolbar',
      'aria-label': 'Text formatting'
    });

    // Formatting buttons
    const formattingGroup = createElement('div', { 
      className: 'komentas-toolbar-group' 
    });

    // Bold button
    const boldBtn = this.createButton('bold', '**B**', 'Bold (Ctrl+B)', () => {
      MarkdownFormatter.formatBold(this.options.textarea);
    });
    this.buttons.set('bold', boldBtn);
    formattingGroup.appendChild(boldBtn);

    // Italic button
    const italicBtn = this.createButton('italic', '_I_', 'Italic (Ctrl+I)', () => {
      MarkdownFormatter.formatItalic(this.options.textarea);
    });
    this.buttons.set('italic', italicBtn);
    formattingGroup.appendChild(italicBtn);

    // Link button
    const linkBtn = this.createButton('link', '🔗', 'Insert link', () => {
      MarkdownFormatter.formatLink(this.options.textarea);
    });
    this.buttons.set('link', linkBtn);
    formattingGroup.appendChild(linkBtn);

    // Unordered list button
    const ulBtn = this.createButton('ul', '•', 'Bullet list', () => {
      MarkdownFormatter.formatUnorderedList(this.options.textarea);
    });
    this.buttons.set('ul', ulBtn);
    formattingGroup.appendChild(ulBtn);

    // Ordered list button
    const olBtn = this.createButton('ol', '1.', 'Numbered list', () => {
      MarkdownFormatter.formatOrderedList(this.options.textarea);
    });
    this.buttons.set('ol', olBtn);
    formattingGroup.appendChild(olBtn);

    // Code button
    const codeBtn = this.createButton('code', '`<>`', 'Inline code', () => {
      MarkdownFormatter.formatCode(this.options.textarea);
    });
    this.buttons.set('code', codeBtn);
    formattingGroup.appendChild(codeBtn);

    // Heading button
    const headingBtn = this.createButton('heading', 'H', 'Heading', () => {
      MarkdownFormatter.formatHeading(this.options.textarea);
    });
    this.buttons.set('heading', headingBtn);
    formattingGroup.appendChild(headingBtn);

    toolbar.appendChild(formattingGroup);

    // Preview button (separate group)
    const previewGroup = createElement('div', { 
      className: 'komentas-toolbar-group' 
    });

    const previewBtn = this.createButton('preview', '👁️ Preview', 'Preview comment', () => {
      this.options.onPreview();
    });
    this.buttons.set('preview', previewBtn);
    previewGroup.appendChild(previewBtn);

    toolbar.appendChild(previewGroup);

    // Help text
    const helpText = createElement('div', {
      className: 'komentas-toolbar-help'
    }, ['Markdown formatting supported']);
    toolbar.appendChild(helpText);

    return toolbar;
  }

  /**
   * Create a toolbar button
   */
  private createButton(
    id: string,
    label: string,
    title: string,
    onClick: () => void
  ): HTMLButtonElement {
    const button = createElement('button', {
      className: `komentas-toolbar-btn komentas-toolbar-btn-${id}`,
      type: 'button',
      title,
      'aria-label': title
    }) as HTMLButtonElement;

    button.innerHTML = label;
    button.addEventListener('click', (e) => {
      e.preventDefault();
      onClick();
    });

    return button;
  }

  /**
   * Setup keyboard shortcuts
   */
  private setupEventListeners(): void {
    this.options.textarea.addEventListener('keydown', (e) => {
      // Ctrl+B for bold
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        MarkdownFormatter.formatBold(this.options.textarea);
      }
      // Ctrl+I for italic
      else if (e.ctrlKey && e.key === 'i') {
        e.preventDefault();
        MarkdownFormatter.formatItalic(this.options.textarea);
      }
      // Ctrl+K for link
      else if (e.ctrlKey && e.key === 'k') {
        e.preventDefault();
        MarkdownFormatter.formatLink(this.options.textarea);
      }
    });
  }

  /**
   * Add CSS styles for toolbar
   */
  private addStyles(): void {
    const isDark = this.options.theme === 'dark';
    
    const styles = `
      .komentas-formatting-toolbar {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px;
        background: ${isDark ? '#1a1a1a' : '#f5f5f5'};
        border: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        border-bottom: none;
        border-radius: 8px 8px 0 0;
        flex-wrap: wrap;
      }

      .komentas-toolbar-group {
        display: flex;
        gap: 4px;
        align-items: center;
      }

      .komentas-toolbar-btn {
        padding: 6px 10px;
        background: ${isDark ? '#2a2a2a' : '#ffffff'};
        border: 1px solid ${isDark ? '#555' : '#ddd'};
        border-radius: 4px;
        color: ${isDark ? '#fff' : '#333'};
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        white-space: nowrap;
      }

      .komentas-toolbar-btn:hover {
        background: ${isDark ? '#333' : '#f0f0f0'};
        border-color: #007cba;
      }

      .komentas-toolbar-btn:active {
        background: ${isDark ? '#444' : '#e0e0e0'};
      }

      .komentas-toolbar-btn:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.4);
      }

      .komentas-toolbar-btn-preview {
        background: #007cba;
        color: white;
        border-color: #007cba;
      }

      .komentas-toolbar-btn-preview:hover {
        background: #005a8b;
        border-color: #005a8b;
      }

      .komentas-toolbar-help {
        font-size: 11px;
        color: ${isDark ? '#888' : '#666'};
        margin-left: auto;
        white-space: nowrap;
      }

      /* Accessibility improvements */
      .komentas-toolbar-btn:focus-visible {
        outline: 2px solid #007cba;
        outline-offset: 2px;
      }

      @media (max-width: 600px) {
        .komentas-formatting-toolbar {
          padding: 6px;
          gap: 6px;
        }

        .komentas-toolbar-btn {
          padding: 5px 8px;
          font-size: 12px;
        }

        .komentas-toolbar-help {
          width: 100%;
          text-align: center;
          margin-left: 0;
          margin-top: 4px;
        }
      }
    `;

    // Check if styles already exist
    if (!document.getElementById('komentas-formatting-toolbar-styles')) {
      const styleSheet = createElement('style', { 
        id: 'komentas-formatting-toolbar-styles' 
      });
      styleSheet.textContent = styles;
      document.head.appendChild(styleSheet);
    }
  }

  /**
   * Get toolbar element
   */
  public getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Enable/disable toolbar buttons
   */
  public setEnabled(enabled: boolean): void {
    this.buttons.forEach((button) => {
      button.disabled = !enabled;
    });
  }

  /**
   * Destroy toolbar and cleanup
   */
  public destroy(): void {
    this.container.remove();
  }
}
